import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { format, isValid, parse, parseISO } from 'date-fns';
import {
  cs,
  da,
  de,
  enGB,
  enUS,
  es,
  fi,
  fr,
  hu,
  it,
  ja,
  ko,
  nb,
  nl,
  pl,
  ptBR,
  ru,
  sv,
  tr,
  zhCN,
  zhTW,
} from 'date-fns/locale';

import { encodeHtml } from '../../utils';
import type { FieldType } from '../field-types';

export const DATE_VALUE_TODAY = 'today';

const DATE_VALUE_REGEXP = /^\d\d\d\d-\d\d-\d\d$/;
const DATE_FORMAT = 'yyyy-MM-dd';

export const dateFnsLocalesMap = {
  de: de,
  'en-GB': enGB,
  'en-US': enUS,
  cs: cs,
  ko: ko,
  'pt-BR': ptBR,
  fr: fr,
  es: es,
  it: it,
  ja: ja,
  nl: nl,
  nb: nb,
  da: da,
  sv: sv,
  fi: fi,
  ru: ru,
  pl: pl,
  tr: tr,
  hu: hu,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'en-pseudo': de,
  xx: de,
  en: enUS,
  'en-ZZ': enUS,
};

const getDateFnsLocale = (locale: string | undefined) => {
  return locale && locale in dateFnsLocalesMap
    ? dateFnsLocalesMap[locale as keyof typeof dateFnsLocalesMap]
    : undefined;
};

const isValidDate = (date: Date): boolean => {
  return !isNaN(date.getTime());
};

/**
 * Returns true if the given date is a valid value for a field of this type.
 */
export function isValidDateString(date: string): boolean {
  return (
    date === DATE_VALUE_TODAY ||
    (!!date.match(DATE_VALUE_REGEXP) && isValid(parse(date, DATE_FORMAT, new Date())))
  );
}

/**
 * Takes a date value, parses it and returns it in YYYY-MM-DD format. If the date string has an invalid
 * format, undefined is returned.
 * @param resolve Unless this is set to false, the "today" virtual value is resolved to the current date.
 */
export function parseDate(date: string | undefined, resolve = true): string | undefined {
  if (!date || !isValidDateString(date)) {
    return undefined;
  } else if (date === DATE_VALUE_TODAY) {
    return resolve ? format(new Date(), DATE_FORMAT) : date;
  } else {
    return date;
  }
}

/**
 * Attempts to convert the given user-defined date string to a valid date. If the date cannot be parsed,
 * undefined is returned.
 */
export function stringToDate(text: string): string | undefined {
  if (!text.trim()) {
    return undefined;
  }

  let date = new Date(text);
  // Convert to local time.
  date = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  if (isNaN(date.getTime())) {
    date = new Date(Number(text)); // Unix timestamp millis
    if (!isNaN(date.getTime()) && date.getFullYear() >= 10000) {
      date = new Date(Number(text) / 1000); // Unix timestamp seconds
    }
  }

  return isNaN(date.getTime()) ? undefined : format(date, DATE_FORMAT);
}

export function toDisplayDate(value: string, locale?: string): string | undefined {
  const dateFnsLocale = getDateFnsLocale(locale);
  return format(parseISO(parseDate(value)!), 'PP', {
    locale: dateFnsLocale,
  });
}

export function getLocalizedDatePlaceholder(locale: string): string {
  const dateFnsLocale = getDateFnsLocale(locale);
  return format(new Date(), 'P', { locale: dateFnsLocale });
}

function getEncodedDate(value: string): string {
  return `<time datetime="${encodeHtml(value)}">${encodeHtml(value)}</time>`;
}

/* Formats a Date object that was created from a date string in ISO format by
 * adjusting for UTC.
 * The Date object is created as a UTC date at midnight but format treats the date
 * as being in local time. So UTC midnight is treated as the previous day for timezones
 * behind UTC. As a workaround, we construct a new Date object in local time based on the UTC date.
 * */
export function formatDateWithUTCOffset(date: Date): string {
  const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return format(utcDate, DATE_FORMAT);
}

/* Parses a date string in ISO format and returns a Date object. We only care
 * about the date as written in the string (ignoring time or timezones).
 * Sometimes parseISO is more lenient than Date constructor (ex. parseISO recognizes
 * YYYY-MMDD as valid while Date constructor does not), so we only treat it as a
 * valid date string if it's recognized by both.
 * We want to use the Date generated by parseISO instead of the Date constructor
 * because parseISO gives us a Date in midnight local time which we need for
 * formatting, while Date constructor with ISO strings gives us a Date in midnight UTC time.
 * */
export function getDateFromISOString(dateString: string): Date | undefined {
  const date = parseISO(dateString.split('T')[0]);
  return isValidDate(date) && isValidDate(new Date(dateString)) ? date : undefined;
}

export function convertStringToISO(value: string): string | undefined {
  if (!value.trim()) {
    return undefined;
  }

  // we need a Date in midnight local time for format to work properly
  let date = getDateFromISOString(value) ?? new Date(value);
  if (isValidDate(date)) {
    return format(date, DATE_FORMAT);
  }

  // check if it's a unix timestamp
  date = new Date(Number(value)); // Unix timestamp millis
  // known issue: unix timestamp seconds import doesn't work
  if (date.getFullYear() >= 10000) {
    date = new Date(Number(value) / 1000); // Unix timestamp seconds
  }
  return isValidDate(date) ? formatDateWithUTCOffset(date) : undefined;
}

const dateFieldType: FieldType<DatabaseFieldType.DATE> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.EMPTY,
    DatabaseFilterType.GREATER,
    DatabaseFilterType.LESS,
  ],

  isCsvImportSupported: true,

  renderExportView: ({ value }) => {
    const parsed = parseDate(value, true);
    if (parsed) {
      return getEncodedDate(parsed);
    } else {
      return '';
    }
  },
  parseFromHtml: (el) =>
    stringToDate(
      el.find('time').eq(0).attr('datetime') ??
        el.find('.date-node').eq(0).attr('data-timestamp') ??
        el.text().trim()
    ),

  getInitialValue: () => undefined,

  getInitialDefaultValue: () => DATE_VALUE_TODAY,

  isEmptyValue: ({ value }) => !value,

  isEqualValue: ({ value: value1 }, { value: value2 }) =>
    parseDate(value1, true) === parseDate(value2, true),

  getStringRepresentation: ({ value }) => parseDate(value, true) ?? '',

  getNumberRepresentation: ({ value }) => {
    const date = parseDate(value, true);
    return date ? parseISO(date).getTime() : undefined;
  },

  convertFromCsv: ({ value }) => {
    return convertStringToISO(value);
  },

  convertFrom: {
    [DatabaseFieldType.TEXT]: ({ value }) => stringToDate(value),
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: ({ value }) => parseDate(value, true) ?? '',
  },
};

export default dateFieldType;
